import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveEmendaStatus } from '../services/emendaService';
import { emendaEtapa } from '../utils/emendaEtapa';
import { STATUS_POR_ROLE } from '../constants/emendaWorkflow';
import { UserRole } from '../types/user.types';

export const CAMARA_AGUARDANDO = 'AGUARDANDO_DEVOLUCAO';
export const CAMARA_EM_APROVACAO = 'EM_APROVACAO';
export const CAMARA_DEVOLVIDA = 'DEVOLVIDA';

export interface FilterableEmenda {
  status?: string;
  statusCicloVida?: string;
  description?: string;
  officialCode?: string;
  institutionName?: string;
  institutionId?: string;
  councilorName?: string;
  councilorId?: string;
  exercicio?: number;
  date?: string;
  objectDetail?: string;
  tipoTransferencia?: string;
  secretariaDestino?: string;
  category?: string;
  classification?: string;
  esfera?: string;
}

interface UseEmendasFilterOptions {
  hasRole: (role: UserRole) => boolean;
  user: unknown;
  isAdmin: boolean;
  isGestor: boolean;
  defaultPageSize?: number;
}

export function useEmendasFilter<T extends FilterableEmenda>(
  emendas: T[],
  { hasRole, user, isAdmin, isGestor, defaultPageSize = 20 }: UseEmendasFilterOptions,
) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [etapaFilter, setEtapaFilter] = useState('Todas');
  const [year, setYear] = useState('');
  const [detailFilter, setDetailFilter] = useState('Todas');
  const [areaFilter, setAreaFilter] = useState('');
  const [esferaFilter, setEsferaFilter] = useState('');
  const [transferenciaFilter, setTransferenciaFilter] = useState('');
  const [camaraFilter, setCamaraFilter] = useState('');
  const [vereador, setVereador] = useState('');
  const [secretaria, setSecretaria] = useState('');
  const [myQueueOnly, setMyQueueOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const myQueueStatuses = useMemo<string[]>(() => {
    // ORCAMENTO is excluded so the role sees all emendas, not just its queue
    for (const role of [UserRole.SECRETARIA, UserRole.CONVENIOS, UserRole.JURIDICO, UserRole.OPERADOR]) {
      if (hasRole(role)) return STATUS_POR_ROLE[role] ?? [];
    }
    return [];
  }, [hasRole]);

  // Roles that always see only their queue — no toggle.
  // SECRETARIA is excluded: it should see everything routed to its own secretaria
  // (already filtered by secretariaDestino below) and only *narrow* to its queue
  // via the optional toggle — acting is gated separately on the emenda screen.
  const isQueueRestricted =
    myQueueStatuses.length > 0 && !isAdmin && !isGestor && !hasRole(UserRole.SECRETARIA);

  const filtered = useMemo(() => {
    return emendas.filter((emenda) => {
      const q = query.trim().toLowerCase();
      // workflowStatus = detailed (statusCicloVida) — used for queue and role-based logic
      const workflowStatus = resolveEmendaStatus(emenda);
      // simplifiedStatus = 5-value simplified (status) — used for the public filter dropdown
      const simplifiedStatus = emenda.status || 'Recebido';
      const yearValue = emenda.exercicio
        ? String(emenda.exercicio)
        : (emenda.date || '').slice(0, 4);

      if (q) {
        const hay = [
          simplifiedStatus,
          workflowStatus,
          emenda.description,
          emenda.officialCode,
          emenda.institutionName,
          emenda.institutionId,
          emenda.councilorName,
          emenda.councilorId,
          emenda.category,
          emenda.classification,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (statusFilter !== 'Todas' && simplifiedStatus !== statusFilter) return false;
      // Filtra pela etapa consolidada (mesma exibida na coluna/cards), não pelo status cru.
      if (etapaFilter !== 'Todas' && emendaEtapa(emenda).label !== etapaFilter) return false;
      if (year && yearValue !== year) return false;
      if (areaFilter) {
        const areaValues = [
          emenda.funcaoCodigo,
          emenda.category,
          emenda.classification,
        ].filter(Boolean).map((value) => String(value));
        if (!areaValues.includes(areaFilter)) return false;
      }
      if (esferaFilter && emenda.esfera !== esferaFilter) return false;
      // Mesma regra do restante do sistema: tudo que não é 'Indireta' é Direta.
      if (transferenciaFilter === 'Indireta' && emenda.tipoTransferencia !== 'Indireta') return false;
      if (transferenciaFilter === 'Direta' && emenda.tipoTransferencia === 'Indireta') return false;
      if (camaraFilter === CAMARA_EM_APROVACAO && resolveEmendaStatus(emenda) !== 'Em aprovação') return false;
      if (camaraFilter === CAMARA_AGUARDANDO && !/^retornada ao or/i.test(emenda.statusCicloVida ?? '')) return false;
      if (camaraFilter === CAMARA_DEVOLVIDA && !emendaEtapa(emenda).devolvida) return false;
      if (vereador && emenda.councilorName !== vereador) return false;
      if (secretaria && emenda.secretariaDestino !== secretaria) return false;
      if (detailFilter === 'Com Detalhamento' && !emenda.objectDetail && !emenda.description) return false;
      if (detailFilter === 'Sem Detalhamento' && (emenda.objectDetail || emenda.description)) return false;

      // Escopo por papel (secretaria/convênios/vereador) já é aplicado pelo backend em
      // GET /emendas/with-details (EmendaService.listWithDetailsForUser) — reimplementar aqui
      // divergia da regra real (o caso SECRETARIA-CNPJ era filtrado por secretariaDestino em vez
      // de vínculo de instituição, escondendo dados que o backend já devolvia corretamente).

      if (isQueueRestricted || myQueueOnly) {
        if (!myQueueStatuses.includes(workflowStatus)) return false;
      }

      return true;
    });
  }, [emendas, query, statusFilter, etapaFilter, year, areaFilter, esferaFilter, transferenciaFilter, camaraFilter, detailFilter, vereador, secretaria, myQueueOnly, isQueueRestricted, myQueueStatuses]);

  const myQueueCount = useMemo(
    () => filtered.filter((e) => myQueueStatuses.includes(resolveEmendaStatus(e))).length,
    [filtered, myQueueStatuses],
  );

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, etapaFilter, year, areaFilter, esferaFilter, transferenciaFilter, camaraFilter, detailFilter, vereador, secretaria, myQueueOnly]);

  const paginatedEmendas = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const clearFilters = useCallback(() => {
    setQuery('');
    setStatusFilter('Todas');
    setEtapaFilter('Todas');
    setYear('');
    setAreaFilter('');
    setEsferaFilter('');
    setTransferenciaFilter('');
    setCamaraFilter('');
    setDetailFilter('Todas');
    setVereador('');
    setSecretaria('');
    setMyQueueOnly(false);
  }, []);

  return {
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    etapaFilter,
    setEtapaFilter,
    year,
    setYear,
    areaFilter,
    setAreaFilter,
    esferaFilter,
    setEsferaFilter,
    transferenciaFilter,
    setTransferenciaFilter,
    camaraFilter,
    setCamaraFilter,
    detailFilter,
    setDetailFilter,
    vereador,
    setVereador,
    secretaria,
    setSecretaria,
    myQueueOnly,
    setMyQueueOnly,
    page,
    setPage,
    pageSize,
    setPageSize,
    filtered,
    paginatedEmendas,
    myQueueCount,
    myQueueStatuses,
    isQueueRestricted,
    clearFilters,
  };
}
